// ui/modules/legal/forms/ContractReviewForm.tsx
import React, { useState, useEffect } from "react";
import { Modal } from "../../../components/Modal";
import { FileUploadKit } from "../../../components/common/FileUploadKit";
import { useAuth } from "../../../context/AuthContext";
import { generateDocCode } from "../../../utils/idGenerator";
import {
  createApprovalRequest,
  updateApprovalRequest,
  uploadApprovalFile,
} from "../../../services/approvalRequestsService";
import {
  createOrResetApprovalFlow,
  resetFlowForResubmission,
  updateStageDecision,
} from "../../../services/approvalFlowService";
import { analyzeUploadedFile } from "../../../services/aiService";
import { getDeptCodeByEmail, isSuperUser } from "../../../config/roles";
import { getDeptNameByCode } from "../../../constants/departments";
import { useToast } from "../../../components/Toast";
import { useApprovalFlow } from "../../../hooks/useApprovalFlow";
import { SystemLinkagePanel } from "../../../components/shared/SystemLinkagePanel";
import { ApprovalFlowPanel } from "../../../components/approval/ApprovalFlowPanel";
import ReadOnlyNotice from "../../../components/common/ReadOnlyNotice";
import type { AttachmentMeta } from "../../../types/attachments";
import firebase from "firebase/compat/app";
import { db } from "../../../../firebaseClient";

interface Props {
  initialData: any | null;
  onClose: () => void;
  onSuccess: () => void;
  allExisting?: any[];
  readOnly?: boolean;
  setFormReadOnly?: React.Dispatch<React.SetStateAction<boolean>>;
  hideFlowPanel?: boolean;
}

export default function ContractReviewForm({
  initialData,
  onClose,
  onSuccess,
  allExisting,
  readOnly = false,
  setFormReadOnly,
  hideFlowPanel = false,
}: Props) {
  const { user } = useAuth();
  const { showToast } = useToast();

  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [uploadedAttachments, setUploadedAttachments] = useState<AttachmentMeta[]>(initialData?.attachments || []);
  const _isSuper = isSuperUser(user?.email);

  const status = initialData?.status || "DRAFT";
  const isLocked = readOnly || (status === "IN_REVIEW" && !!initialData?.id);
  const isResubmitting = ["RETURNED", "REJECTED"].includes(status);

  const { flow, loading: flowLoading } = useApprovalFlow("CONTRACT_REVIEW", initialData?.id || "new");

  const [form, setForm] = useState({
    department: initialData?.department || getDeptNameByCode(getDeptCodeByEmail(user?.email)),
    handlerName: initialData?.handlerName || user?.displayName || "",
    title: initialData?.title || "",
    counterparty: initialData?.counterparty || "",
    contractType: initialData?.contractType || "",
    priority: initialData?.priority || "normal",
    description: initialData?.description || "",
    docCode: initialData?.docCode || "",
    contractAmount: initialData?.contractAmount ? Number(initialData.contractAmount) : 0,
    contractCurrency: initialData?.contractCurrency || "TWD",
    relatedNos: initialData?.relatedNos || {},
    linkKey: initialData?.linkKey || "",
  });

  useEffect(() => {
    if (!initialData && !form.docCode) {
      setForm((p) => ({ ...p, docCode: generateDocCode("contract", new Date(), allExisting || []) }));
    }
  }, []);

  // ---- ✅ 駁回紀錄寫入：updateStageDecision 之後 ----
  const handleDecision = async (
    stageKey: any,
    decision: any,
    comment?: string,
    deptCode?: string,
    isDirectSign?: boolean
  ) => {
    if (!flow) return;
    const safeComment = comment ?? "";

    try {
      await updateStageDecision({
        flowId: (flow as any).id,
        stageKey,
        decision,
        comment: safeComment,
        actorUid: (user as any)?.id || (user as any)?.uid || "",
        actorName: user?.displayName || "User",
        actorEmail: user?.email || "",
        deptCode,
        isDirectSign,
      });

      // --- 駁回紀錄 ---
      if (decision === "REJECTED" || decision === "RETURNED") {
        const rejectNote = {
          decidedAt: new Date().toISOString(),
          decidedBy: user?.displayName || "User",
          reason: safeComment || "(未填寫)",
        };

        const mainRef = db.collection("approval_requests").doc((flow as any).id.replace("CONTRACT_REVIEW_", ""));
        await mainRef.update({
          lastRejectedAt: rejectNote.decidedAt,
          lastRejectedBy: rejectNote.decidedBy,
          lastRejectReason: rejectNote.reason,
          rejectHistory: firebase.firestore.FieldValue.arrayUnion(rejectNote),
          status: "REJECTED",
        });
      }

      showToast("簽核操作成功");
    } catch (e) {
      console.error(e);
      showToast("簽核操作失敗", "error");
    }
  };

  

      // ---- ✨ AI 自動回填（合約審理）----
      const handleAiAutoFill = async () => {
        if (aiLoading) return;
        if (isLocked) {
          showToast("案件簽核中，無法使用 AI。", "warning");
          return;
        }
        if (!pendingFiles?.length && !uploadedAttachments?.length) {
          showToast("請先上傳附件後再按 AI。", "warning");
          return;
        }

        try {
          setAiLoading(true);

          const ctx = {
            title: form.title,
            counterparty: form.counterparty,
            contractType: form.contractType,
            priority: form.priority,
            contractAmount: form.contractAmount,
            contractCurrency: form.contractCurrency,
            department: form.department,
            handlerName: form.handlerName,
          };

          const firstUploaded: any = Array.isArray(uploadedAttachments) ? uploadedAttachments[0] : null;

const res: any = await analyzeUploadedFile({
  // ✅ 兼容不同版本的 aiService：同時提供 files/rawFiles/url/fileMeta
  mode: "analysis",
	  featureId: "contractReview.analysis",
  contextType: "CONTRACT_REVIEW",
  contextId: String(initialData?.id || form.docCode || "new"),

  files: pendingFiles,
  rawFiles: pendingFiles,

  url: firstUploaded?.downloadUrl || firstUploaded?.url,
  fileMeta: firstUploaded,

  userInstruction: [
    "你是台灣企業法務助理（某某公司）。請嚴格依『附件內容』分析，不得憑空臆測或套模板。",
    "請輸出：",
    "A) 合約內容概述（標的/期間/主要義務）",
    "B) 特別注意條款（至少包含付款節點、驗收/交付、違約/解除、責任限制/賠償、保密/個資、智慧財產、爭議解決）",
    "C) 對某某公司公司的風險／利弊評估（高/中/低 + 原因）",
    "D) 建議修改/補強（用條列，必要時給替代文字）",
    "E) 待確認問題清單",
    "目前表單（供參考）：",
    JSON.stringify(ctx, null, 2),
  ].join("\n"),

} as any);

          if (!res?.ok) {
            showToast(res?.message || "AI 分析失敗", "error");
            return;
          }

          const summary = String(res?.summary || "").trim();
          const risksArr = Array.isArray(res?.keyRisks) ? res.keyRisks : [];
          const risksText = risksArr
            .map((r: any) => {
              const sev = r?.severity ? `【${String(r.severity).trim()}】` : "";
              const t = String(r?.title || "").trim();
              const d = String(r?.description || "").trim();
              const head = [sev, t].filter(Boolean).join("");
              return head ? `• ${head}${d ? `：${d}` : ""}` : "";
            })
            .filter(Boolean)
            .join("\n");

	          const riskEvalText = String(res?.riskAssessmentText || "").trim();
	          const combined = [
	            summary,
	            risksText ? `\n\n【風險提示】\n${risksText}` : "",
	            riskEvalText ? `\n\n${riskEvalText}` : "",
	          ]
	            .join("")
	            .trim();

          // ---- ✅ 金額/幣別回填（合約總價額）----
          const pickFirstText = (...vals: any[]) => {
            for (const v of vals) {
              if (v == null) continue;
              const s = String(v).trim();
              if (s) return s;
            }
            return "";
          };

          const normalizeCurrency = (v: any): string => {
            const s = String(v ?? "").toUpperCase();
            if (!s) return "";
            if (s.includes("TWD") || s.includes("NTD") || s.includes("NT$") || s.includes("台幣") || s.includes("新台幣")) return "TWD";
            if (s.includes("USD") || s.includes("US$") || s.includes("美金")) return "USD";
            if (s.includes("JPY") || s.includes("日幣") || s.includes("円")) return "JPY";
            if (s.includes("KRW") || s.includes("韓圜") || s.includes("WON")) return "KRW";
            if (s.includes("OTHER") || s.includes("其他")) return "OTHER";
            return "";
          };

          const parseAmountNumber = (v: any): number => {
            const raw = String(v ?? "");
            // 支援「30萬」「0.3萬」「1.2億」這種寫法
            const mWan = raw.match(/(\d+(?:\.\d+)?)\s*萬/);
            if (mWan) return Math.round(Number(mWan[1]) * 10000);
            const mYi = raw.match(/(\d+(?:\.\d+)?)\s*億/);
            if (mYi) return Math.round(Number(mYi[1]) * 100000000);

            const s = raw
              .replace(/[\s,，]/g, "")
              .replace(/(新台幣|台幣|NTD|TWD|NT\$|US\$|USD|JPY|KRW|元|整|圓)/gi, "");
            const m = s.match(/-?\d+(?:\.\d+)?/);
            const n = m ? Number(m[0]) : 0;
            return Number.isFinite(n) ? n : 0;
          };

          const extractAmountFromText = (text: string): { amount: number; currency: string } => {
            const t = String(text || "");
            const lines = t.split(/\r?\n/).map((x) => x.trim()).filter(Boolean);

            const keywordRe = /(總價|價金|對價|酬金|報酬|服務費|費用|付款|金額|固定酬金|payment|fee|amount)/i;
            const pickCurrencyFromLine = (line: string) => {
              const c = normalizeCurrency(line);
              if (c) return c;
              if (/(新台幣|台幣|NT\$|NTD|TWD)/i.test(line)) return "TWD";
              if (/(US\$|USD|美金)/i.test(line)) return "USD";
              if (/(JPY|日幣|円)/i.test(line)) return "JPY";
              if (/(KRW|韓圜|WON)/i.test(line)) return "KRW";
              return "";
            };

            for (const line of lines) {
              if (!keywordRe.test(line)) continue;
              const n = parseAmountNumber(line);
              if (n > 0) return { amount: n, currency: pickCurrencyFromLine(line) || "TWD" };
            }

            // fallback：有幣別或「元」的數字
            for (const line of lines) {
              if (!/(元|圓|NT\$|NTD|TWD|US\$|USD|JPY|KRW|新台幣|台幣|美金|日幣|韓圜)/i.test(line)) continue;
              const n = parseAmountNumber(line);
              if (n > 0) return { amount: n, currency: pickCurrencyFromLine(line) || "TWD" };
            }

            return { amount: 0, currency: "" };
          };

          const amountCandidate = pickFirstText(
            (res as any)?.contractAmount,
            (res as any)?.amount,
            (res as any)?.totalAmount,
            (res as any)?.fields?.amount,
            (res as any)?.raw?.fields?.amount,
            (res as any)?.raw?.amount
          );
          const currencyCandidate = pickFirstText(
            (res as any)?.contractCurrency,
            (res as any)?.currency,
            (res as any)?.fields?.currency,
            (res as any)?.raw?.fields?.currency
          );

          let parsedAmount = parseAmountNumber(amountCandidate);
          let parsedCurrency = normalizeCurrency(currencyCandidate);

          // 如果 AI 沒給結構化金額，就從 summary/combined 文字抽
          if (!parsedAmount) {
            const ext = extractAmountFromText([summary, combined].filter(Boolean).join("\n"));
            parsedAmount = ext.amount;
            if (!parsedCurrency) parsedCurrency = ext.currency;
          }


          setForm((prev: any) => {
            const next = { ...prev };
            if (!next.title && res?.title) next.title = String(res.title || "");
            if (!next.counterparty && res?.counterparty) next.counterparty = String(res.counterparty || "");
            // ✅ 回填合約總價額 / 幣別（只有在目前表單還是空值時才寫入）
            if ((!next.contractAmount || Number(next.contractAmount) === 0) && parsedAmount > 0) {
              next.contractAmount = Number(parsedAmount);
            }
            if (parsedCurrency) {
              const cur = String(next.contractCurrency || "").toUpperCase();
              // 預設多為 TWD；若 AI 抽到 USD/JPY/KRW 等，允許覆蓋預設
              if (!cur || cur === "TWD" || cur === "OTHER") {
                next.contractCurrency = parsedCurrency;
              }
            }
            if (combined) {
              next.description = String(prev.description || "").trim()
                ? `${String(prev.description).trim()}\n\n${combined}`
                : combined;
            }
            return next;
          });

          showToast("AI 已產生草稿（已回填至「說明/擬辦」）。");
        } catch (e: any) {
          console.error(e);
          showToast(`AI 服務連線失敗：${e?.message || "unknown error"}`, "error");
        } finally {
          setAiLoading(false);
        }
      };

// ---- ✅ 重新送出簽核 ----
  const handleSubmit = async (submitNow: boolean) => {
    if (isLocked) {
      showToast("案件簽核中，無法修改。", "warning");
      return;
    }
    if (!form.title || !form.handlerName) return alert("請填寫標題與承辦人");

    setLoading(true);
    try {
      let finalAttachments = [...uploadedAttachments];
      if (pendingFiles.length > 0) {
        for (const file of pendingFiles) {
          finalAttachments.push(await uploadApprovalFile(initialData?.id || "temp", "request", file, user?.email || ""));
        }
      }

      const payload: any = {
        ...form,
        module: "contractDraft",
        attachments: finalAttachments,
        contractAmount: form.contractAmount ? Number(form.contractAmount) : null,
        requesterName: form.handlerName,
        historyPolicyVersion: 2,
        updatedAt: new Date().toISOString(),
      };

      let docId = initialData?.id;
      if (docId) {
        await updateApprovalRequest(docId, {
          ...payload,
          status: submitNow ? "IN_REVIEW" : initialData?.status || "DRAFT",
        });

        // 🔁 若為駁回後再送出
        if (submitNow && isResubmitting) {
          await resetFlowForResubmission(`CONTRACT_REVIEW_${docId}`);
          await db.collection("approval_requests").doc(docId).update({
            resubmittedAt: new Date().toISOString(),
            resubmittedBy: user?.displayName || "User",
            status: "IN_REVIEW",
          });
        }
      } else {
        const newId = await createApprovalRequest({
          ...payload,
          requesterUid: (user as any)?.id || (user as any)?.uid || "",
          requesterEmail: user?.email || "",
          sensitivity: "normal",
          status: submitNow ? "IN_REVIEW" : "DRAFT",
        });
        docId = newId;
        if (submitNow) {
          await createOrResetApprovalFlow(
            "CONTRACT_REVIEW",
            newId,
            (user as any)?.id || (user as any)?.uid || "",
            form.department,
            form.handlerName
          );
        }
      }

      showToast(submitNow ? "已送出簽核" : "草稿已儲存");
      onSuccess();
    } catch (e: any) {
      console.error(e);
      showToast(`儲存失敗：${e?.message || e}`, "error");
    } finally {
      setLoading(false);
    }
  };

  const rejectHistory = (initialData as any)?.rejectHistory;
  const hasRejectHistory = Array.isArray(rejectHistory) && rejectHistory.length > 0;
  const showFlowActions = !hideFlowPanel && !!flow && !readOnly;
  const showSidePanel = !hideFlowPanel && (hasRejectHistory || showFlowActions);

  const baseTitle =
    status === "IN_REVIEW"
      ? "簽核中（不可變更）"
      : isResubmitting
      ? "駁回後修正補充"
      : initialData
      ? readOnly
        ? "合約審理（檢視）"
        : "編輯案件"
      : "提出合約會審";

  const titleSuffix =
    form.docCode || form.title
      ? `：${form.docCode || ""}${form.title ? `｜${form.title}` : ""}`
      : "";

  return (
    <Modal
      title={baseTitle + titleSuffix}
      onClose={onClose}
      width={showSidePanel ? 1200 : 850}
    >
      <div
        style={{
          background: "#0f0f0f",
          color: "#f5f5f5",
          padding: 20,
          borderRadius: 12,
          display: "grid",
          gridTemplateColumns: showSidePanel ? "1fr 380px" : "1fr",
          gap: 24,
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {status === "IN_REVIEW" && (
            <div
              style={{
                background: "#2f2f1f",
                color: "#facc15",
                padding: "10px 14px",
                borderRadius: 8,
                fontWeight: "bold",
              }}
            >
              🟡 簽核中，不得變更內容
            </div>
          )}

          {initialData?.lastRejectReason && (
            <div
              style={{
                background: "#2f1f1f",
                color: "#ff8080",
                padding: "10px 14px",
                borderRadius: 8,
                marginBottom: 8,
              }}
            >
              <div style={{ fontWeight: "bold" }}>上次駁回原因：</div>
              <div>{initialData.lastRejectReason}</div>
              <div style={{ fontSize: 12, opacity: 0.7 }}>
                {initialData.lastRejectedBy}（
                {new Date(initialData.lastRejectedAt).toLocaleString("zh-TW")}）
              </div>
            </div>
          )}

          {isLocked && <ReadOnlyNotice />}

          <SystemLinkagePanel
            relatedNos={form.relatedNos}
            linkKey={form.linkKey}
            onChange={({ relatedNos, linkKey }: any) => setForm({ ...form, relatedNos, linkKey })}
          />

          <div className="grid">
            <div className="field">
              <label className="label">單號</label>
              <input
                className="input"
                value={form.docCode}
                readOnly
                disabled
                style={{ background: "#1a1a1a", color: "#fff" }}
              />
            </div>

            <div className="field">
              <label className="label">承辦部門</label>
              <input
                className="input"
                value={form.department}
                readOnly
                disabled
                style={{ background: "#1a1a1a", color: "#fff" }}
              />
            </div>

            <div className="field">
              <label className="label">承辦人 *</label>
              <input
                className="input"
                value={form.handlerName}
                onChange={(e) => setForm({ ...form, handlerName: e.target.value })}
                disabled={isLocked}
              />
            </div>

            <div className="field" style={{ gridColumn: "1/-1" }}>
              <label className="label">案件名稱 *</label>
              <input
                className="input"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                disabled={isLocked}
              />
            </div>

            <div className="field">
              <label className="label">對象</label>
              <input
                className="input"
                value={form.counterparty}
                onChange={(e) => setForm({ ...form, counterparty: e.target.value })}
                disabled={isLocked}
              />
            </div>

            <div className="field">
              <label className="label">審理時間需求</label>
              <select
                className="input"
                value={form.priority}
                onChange={(e) => setForm({ ...form, priority: e.target.value })}
                disabled={isLocked}
              >
                <option value="normal">普通件（3–5天）</option>
                <option value="urgent">急件（1–3天）</option>
                <option value="super_urgent">特急件（1天）</option>
              </select>
            </div>

            <div className="field">
              <label className="label">合約總價額幣別</label>
              <select
                className="input"
                value={form.contractCurrency}
                onChange={(e) => setForm({ ...form, contractCurrency: e.target.value })}
                disabled={isLocked}
              >
                <option value="TWD">新台幣</option>
                <option value="USD">美金</option>
                <option value="JPY">日幣</option>
                <option value="KRW">韓圜</option>
                <option value="OTHER">其他</option>
              </select>
            </div>

            <div className="field">
              <label className="label">合約總價額</label>
              <input
                className="input"
                type="number"
                value={Number.isFinite(form.contractAmount) && form.contractAmount !== 0 ? String(form.contractAmount) : ""}
                onChange={(e) => setForm({ ...form, contractAmount: Number(e.target.value || 0) })}
                disabled={isLocked}
                placeholder="輸入金額"
              />
            </div>

            <div className="field" style={{ gridColumn: "1/-1" }}>
              <label className="label">說明 / 擬辦</label>
              <textarea
                className="input"
                rows={4}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                disabled={isLocked}
              />
            </div>
          </div><div style={{ position: "relative" }}>
  {!isLocked && (
    <div style={{ position: "absolute", right: 0, top: -6, zIndex: 2 }}>
      <button
        type="button"
        onClick={handleAiAutoFill}
        disabled={aiLoading}
        title={aiLoading ? "AI 產生中..." : "AI 自動回填（草擬）"}
        className="btn"
        style={{
          width: "auto",
          padding: "6px 10px",
          background: "linear-gradient(90deg, #9b5cff, #ff3ea5)",
          color: "#fff",
          border: "none",
          borderRadius: 999,
          fontSize: 12,
          display: "flex",
          alignItems: "center",
          gap: 6,
          opacity: aiLoading ? 0.7 : 1,
          cursor: aiLoading ? "not-allowed" : "pointer",
        }}
      >
        ✨ AI 自動回填
      </button>
    </div>
  )}

  <FileUploadKit
    label="附件"
    existingFiles={uploadedAttachments}
    rawFiles={pendingFiles}
    onFilesSelected={(fs: File[]) => setPendingFiles((p) => [...p, ...fs])}
    onRemoveRawFile={(index: number) => setPendingFiles((p) => p.filter((_, i) => i !== index))}
    disabled={isLocked}
  />
</div>
{!isLocked && (
            <div className="row" style={{ marginTop: 24, justifyContent: "flex-end", gap: 12 }}>
              <button
                className="btn"
                style={{ background: "transparent", border: "1px solid #fff", color: "#fff" }}
                onClick={() => handleSubmit(false)}
                disabled={loading}
              >
                儲存草稿
              </button>
              <button
                className="btn"
                style={{ background: "#8b5cf6", color: "#fff" }}
                onClick={() => handleSubmit(true)}
                disabled={loading}
              >
                送出簽核
              </button>
            </div>
          )}
        </div>

        {showSidePanel && (
          <div style={{ borderLeft: "1px solid #333", paddingLeft: 24 }}>
            {/* 右側不再重複顯示簽核進度（進度已由專用「簽核流程」視窗呈現） */}

            {/* 歷次駁回紀錄（若有） */}
            {hasRejectHistory && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ color: "#fbbf24", fontWeight: 900, marginBottom: 10 }}>歷次駁回紀錄</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {(rejectHistory as any)
                    .slice()
                    .reverse()
                    .map((r: any, idx: number) => (
                      <div
                        key={idx}
                        style={{
                          background: "rgba(255,255,255,0.04)",
                          border: "1px solid rgba(255,255,255,0.10)",
                          borderRadius: 12,
                          padding: "10px 12px",
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                          <div style={{ fontWeight: 900, color: "#fff" }}>{r?.decidedBy || "(未知)"}</div>
                          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.55)" }}>
                            {r?.decidedAt ? new Date(r.decidedAt).toLocaleString("zh-TW") : ""}
                          </div>
                        </div>
                        <div style={{ marginTop: 6, color: "rgba(255,255,255,0.85)", fontSize: 13 }}>
                          {r?.reason || "(未填寫原因)"}
                        </div>

                        {r?.snapshot && (
                          <div
                            style={{
                              marginTop: 8,
                              background: "rgba(0,0,0,0.25)",
                              border: "1px solid rgba(255,255,255,0.10)",
                              borderRadius: 12,
                              padding: "10px 12px",
                            }}
                          >
                            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.60)", marginBottom: 6 }}>
                              當次內容快照
                            </div>
                            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.85)", lineHeight: 1.6 }}>
                              {(r.snapshot.docCode || "") && (
                                <div>編號：{r.snapshot.docCode}</div>
                              )}
                              {(r.snapshot.title || "") && (
                                <div>案件：{r.snapshot.title}</div>
                              )}
                              {(r.snapshot.counterparty || "") && (
                                <div>對象：{r.snapshot.counterparty}</div>
                              )}
                              {(r.snapshot.priority || "") && (
                                <div>審理時程：{r.snapshot.priority}</div>
                              )}
                              {(r.snapshot.contractAmount != null || r.snapshot.contractCurrency) && (
                                <div>
                                  總價：{r.snapshot.contractCurrency || ""} {r.snapshot.contractAmount || ""}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                </div>
              </div>
            )}

            {showFlowActions && (
              <>
                <h4 style={{ color: "#8b5cf6", marginBottom: 16 }}>簽核操作</h4>
                {flowLoading ? (
                  <div className="p">讀取中...</div>
                ) : (
                  <ApprovalFlowPanel flow={flow as any} onDecision={handleDecision} showStages={false} />
                )}
              </>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
